import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { DataGrid } from '../components/DataGrid';
import { Ticket, PlugsConnected, X, PaperPlaneTilt, ArrowsClockwise, Robot } from '@phosphor-icons/react';

const errMsg = (e) => e?.response?.data?.detail || 'Something went wrong';
const inp = 'border border-slate-200 rounded-md px-2.5 py-2 text-sm focus:border-orange-500 focus:outline-none';
const StatusChip = ({ s }) => <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${{ open: 'bg-sky-100 text-sky-700', pending: 'bg-amber-100 text-amber-700', resolved: 'bg-green-100 text-green-700', closed: 'bg-slate-200 text-slate-600' }[s]}`}>{s}</span>;
const SyncChip = ({ s }) => <span className={`text-[10px] px-2 py-0.5 rounded-full ${s === 'synced' ? 'bg-green-50 text-green-600' : s === 'sync_failed' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-500'}`}>{s === 'sync_failed' ? 'sync failed' : s}</span>;

const ConnectorCard = ({ provider, meta, conn, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ base_url: conn?.base_url || '', mode: conn?.mode || 'sandbox', creds: {} });
  const save = async () => {
    try {
      await api.post('/itsm/connectors', { provider, base_url: f.base_url, mode: f.mode, credentials: Object.keys(f.creds).length ? f.creds : null });
      toast.success(`${meta.label} saved (${f.mode})`); setOpen(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    try { const { data } = await api.post(`/itsm/connectors/${conn.id}/test`); data.ok ? toast.success(data.note) : toast.error(data.note); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`itsm-conn-${provider}`}>
      <div className="flex items-center justify-between">
        <p className="font-display font-bold text-sm">{meta.label}</p>
        {conn ? <span className={`text-[10px] px-2 py-0.5 rounded-full ${conn.status === 'healthy' ? 'bg-green-100 text-green-700' : conn.status === 'error' ? 'bg-rose-100 text-rose-700' : 'bg-sky-100 text-sky-700'}`}>{conn.mode} · {conn.status}</span>
          : <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-400">not configured</span>}
      </div>
      {conn?.last_test && <p className="text-xs text-slate-400 mt-1 truncate" title={conn.last_test.note}>{conn.last_test.note}</p>}
      <div className="flex gap-2 mt-3">
        <button data-testid={`itsm-conn-${provider}-configure`} onClick={() => setOpen(!open)} className="text-xs border border-slate-200 rounded px-2.5 py-1.5 hover:border-orange-500">{conn ? 'Edit' : 'Configure'}</button>
        {conn && <button data-testid={`itsm-conn-${provider}-test`} onClick={test} className="text-xs bg-slate-900 text-white rounded px-2.5 py-1.5 hover:bg-slate-800">Test connection</button>}
      </div>
      {open && (
        <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
          <select data-testid={`itsm-conn-${provider}-mode`} className={`${inp} w-full`} value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })}>
            <option value="sandbox">Sandbox - simulated, test everything now</option>
            <option value="live">Live - real API calls (needs credentials)</option>
          </select>
          <input className={`${inp} w-full`} placeholder={meta.url_hint} value={f.base_url} onChange={(e) => setF({ ...f, base_url: e.target.value })} />
          {f.mode === 'live' && meta.auth_fields.map((k) => (
            <input key={k} type={/token|key|password/.test(k) ? 'password' : 'text'} className={`${inp} w-full`} placeholder={k.replace('_', ' ')}
              value={f.creds[k] || ''} onChange={(e) => setF({ ...f, creds: { ...f.creds, [k]: e.target.value } })} />))}
          <button data-testid={`itsm-conn-${provider}-save`} onClick={save} className="bg-slate-900 text-white text-xs rounded px-3 py-1.5 hover:bg-slate-800">Save connector</button>
        </div>)}
    </div>
  );
};

const TicketDrawer = ({ t, onClose, refresh }) => {
  const [body, setBody] = useState('');
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/40" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="itsm-ticket-drawer">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono-label text-[10px] uppercase text-slate-400">{t.provider} · {t.external_id || 'no external id'}</p>
            <p className="font-display font-extrabold text-lg">{t.subject}</p>
          </div>
          <button onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="flex items-center gap-2 mt-1 mb-3"><StatusChip s={t.status} /><SyncChip s={t.sync_status} /><span className="text-xs text-slate-400 capitalize">{t.priority} priority</span></div>
        <p className="text-sm text-slate-600 mb-4">{t.description}</p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {['open', 'pending', 'resolved', 'closed'].filter((s) => s !== t.status).map((s) => (
            <button key={s} data-testid={`itsm-status-${s}`} onClick={() => act(() => api.post(`/itsm/tickets/${t.id}/status`, { status: s }), `Status: ${s}`)}
              className="text-xs border border-slate-200 rounded-full px-3 py-1 capitalize hover:border-orange-500">{s}</button>))}
          <button data-testid="itsm-simulate-btn" onClick={() => act(() => api.post(`/itsm/tickets/${t.id}/simulate`), 'Remote agent update pulled')}
            className="text-xs border border-violet-200 bg-violet-50 text-violet-700 rounded-full px-3 py-1 flex items-center gap-1"><Robot size={12} />Simulate agent reply</button>
          {t.sync_status === 'sync_failed' && (
            <button data-testid="itsm-retry-btn" onClick={() => act(() => api.post(`/itsm/tickets/${t.id}/retry`), 'Retried')}
              className="text-xs border border-rose-200 bg-rose-50 text-rose-600 rounded-full px-3 py-1 flex items-center gap-1"><ArrowsClockwise size={12} />Retry sync</button>)}
        </div>
        <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1.5">Conversation</p>
        <div className="space-y-2 mb-3">
          {(t.comments || []).map((c) => (
            <div key={c.id} className={`rounded-lg p-2.5 text-sm ${c.source === 'remote' ? 'bg-violet-50 border border-violet-100' : 'bg-slate-50 border border-slate-100'}`}>
              <p className="text-xs text-slate-400 mb-0.5">{c.by} · {(c.at || '').slice(0, 16).replace('T', ' ')}{!c.public && ' · internal'}</p>{c.body}
            </div>))}
          {(t.comments || []).length === 0 && <p className="text-xs text-slate-400">No comments yet.</p>}
        </div>
        <div className="flex gap-2 mb-5">
          <input data-testid="itsm-comment-input" className={`${inp} flex-1`} placeholder="Add a comment..." value={body} onChange={(e) => setBody(e.target.value)} />
          <button data-testid="itsm-comment-btn" disabled={!body.trim()} onClick={() => act(() => api.post(`/itsm/tickets/${t.id}/comment`, { body }).then(() => setBody('')), 'Comment synced')}
            className="bg-slate-900 text-white rounded-md px-3 disabled:opacity-40"><PaperPlaneTilt size={15} /></button>
        </div>
        <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1.5">Sync log</p>
        {(t.sync_log || []).slice().reverse().map((l, i) => (
          <p key={i} className="text-xs py-1 border-t border-slate-50"><span className={l.ok ? 'text-green-600' : 'text-rose-600'}>{l.ok ? 'OK' : 'FAIL'}</span> <b>{l.op}</b> · {(l.at || '').slice(0, 19).replace('T', ' ')} - {l.note}</p>))}
      </div>
    </div>
  );
};

export default function ITSM() {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState(null);
  const [nf, setNf] = useState({ connector_id: '', subject: '', description: '', priority: 'medium' });
  const { data: provs } = useQuery({ queryKey: ['itsm-providers'], queryFn: () => api.get('/itsm/providers').then((r) => r.data) });
  const { data: conns } = useQuery({ queryKey: ['itsm-conns'], queryFn: () => api.get('/itsm/connectors').then((r) => r.data) });
  const { data: tickets, isLoading: tLoading } = useQuery({ queryKey: ['itsm-tickets'], queryFn: () => api.get('/itsm/tickets').then((r) => r.data) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['itsm-conns'] }); qc.invalidateQueries({ queryKey: ['itsm-tickets'] }); };
  const createTicket = async () => {
    try { const { data } = await api.post('/itsm/tickets', nf); toast.success(`Ticket ${data.external_id || ''} created`); setNf({ ...nf, subject: '', description: '' }); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const active = (tickets || []).find((t) => t.id === activeId);

  return (
    <div className="space-y-5" data-testid="itsm-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2"><Ticket size={26} className="text-orange-600" />ITSM & Tickets</h1>
        <p className="text-sm text-slate-500">Freshservice, Zendesk, Jira Service Management and ServiceNow - sandbox mode works end to end today; plug real keys in per connector when ready.</p>
      </div>

      <div>
        <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2 flex items-center gap-1"><PlugsConnected size={12} />Connectors</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {provs && Object.entries(provs).map(([key, meta]) => (
            <ConnectorCard key={key} provider={key} meta={meta} conn={(conns || []).find((c) => c.provider === key)} onSaved={refresh} />))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="itsm-new-ticket">
        <p className="font-display font-bold mb-3">Raise a ticket</p>
        <div className="grid sm:grid-cols-4 gap-2">
          <select data-testid="itsm-t-connector" className={inp} value={nf.connector_id} onChange={(e) => setNf({ ...nf, connector_id: e.target.value })}>
            <option value="">Pick a connector</option>
            {(conns || []).map((c) => <option key={c.id} value={c.id}>{c.name} ({c.mode})</option>)}
          </select>
          <input data-testid="itsm-t-subject" className={`${inp} sm:col-span-2`} placeholder="Subject" value={nf.subject} onChange={(e) => setNf({ ...nf, subject: e.target.value })} />
          <select data-testid="itsm-t-priority" className={inp} value={nf.priority} onChange={(e) => setNf({ ...nf, priority: e.target.value })}>
            {['low', 'medium', 'high', 'urgent'].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <textarea data-testid="itsm-t-desc" className={`${inp} sm:col-span-4`} rows={2} placeholder="Describe the issue..." value={nf.description} onChange={(e) => setNf({ ...nf, description: e.target.value })} />
        </div>
        <button data-testid="itsm-t-create" disabled={!nf.connector_id || !nf.subject.trim() || !nf.description.trim()} onClick={createTicket}
          className="mt-3 bg-slate-900 hover:bg-slate-800 text-white text-sm rounded-md px-4 py-2 disabled:opacity-50">Create & sync ticket</button>
      </div>

      <DataGrid gridId="itsm-tickets" rows={tickets || []} loading={tLoading} onRowClick={(r) => setActiveId(r.id)} exportName="itsm_tickets"
        columns={[
          { key: 'external_id', label: 'Ref', render: (r) => <b>{r.external_id || '-'}</b> },
          { key: 'subject', label: 'Subject' },
          { key: 'provider', label: 'Provider', render: (r) => <span className="capitalize">{r.provider}</span> },
          { key: 'priority', label: 'Priority', render: (r) => <span className="capitalize">{r.priority}</span> },
          { key: 'status', label: 'Status', render: (r) => <StatusChip s={r.status} /> },
          { key: 'sync_status', label: 'Sync', render: (r) => <SyncChip s={r.sync_status} /> },
          { key: 'requester', label: 'Requester', hidden: true },
          { key: 'created_at', label: 'Created', render: (r) => (r.created_at || '').slice(0, 10) },
        ]}
        filters={[
          { key: 'provider', label: 'Provider', options: ['freshservice', 'zendesk', 'jira', 'servicenow'] },
          { key: 'status', label: 'Status', options: ['open', 'pending', 'resolved', 'closed'] },
          { key: 'sync_status', label: 'Sync', options: ['synced', 'sync_failed', 'pending'] },
        ]}
        emptyText="No tickets yet - configure a connector above (sandbox is instant) and raise your first ticket." />
      {active && <TicketDrawer t={active} onClose={() => setActiveId(null)} refresh={refresh} />}
    </div>
  );
}
